import React, { useState, useRef, useEffect } from 'react';
import { ArrowUp, House, FlagCheckered, UserCircle } from '@phosphor-icons/react';
import { suggestsRaceChange } from '../utils/raceAction';
import { profileSectionFor } from '../utils/profileAction';
import authFetch from '../utils/authFetch';
import { watchPlanUpdateJob } from '../utils/planUpdateJob';
import CoachNote from '../components/ui/CoachNote';
import { saveWait, loadWait, clearWait, findReply, nextDelay, fetchWithTimeout } from '../utils/deepAnalysisWait';
import PlanProposalCard from '../components/chat/PlanProposalCard';
import InjuryStateOffer from '../components/chat/InjuryStateOffer';
import SafetyHoldBanner from '../components/chat/SafetyHoldBanner';
import { useSafetyHold } from '../utils/safetyHold';
import { fetchOpenProposal, approveProposal, rejectProposal, watchProposal, RESUMABLE_PROPOSAL_STATUSES } from '../utils/planProposal';
import { DailyLimitCard, QuotaApproaching, QuotaCounter } from '../components/chat/DailyLimit';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://claude-coach-backend-1.onrender.com';

// The coach's last text message before a proposal card (cards / CTAs in between are skipped; a user message stops).
function coachTextBefore(messages, idx) {
  for (let i = idx - 1; i >= 0; i--) {
    const m = messages[i];
    if (m.isUser) return undefined;
    if (m.text) return m.text;
  }
  return undefined;
}

// (The limit card moved to components/chat/DailyLimit.jsx — 2026-10-30: it takes the input's place.)

function Chat({ onBack, userId, profileComplete, fromOnboarding, onNavigateToWeeklyPlan, onNavigateToRaces, onNavigateToProfile, todayActivity, injuryArea, nextRace, weeklyPlanPhase, sportsActive = ['running'] }) {
  const [messages, setMessages] = useState([]);
  // Safety hold (2026-10-26): medical red flags reported -> plan changes held by the server until a medical release.
  // Loaded on mount (a refresh shows it again) and from chat replies; released only through the server.
  const safety = useSafetyHold(authFetch, userId);
  const israelToday = new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Jerusalem' });
  const handleSafetyRelease = async (form) => {
    const out = await safety.release(form);
    if (out.ok) {
      setMessages(prev => [...prev, {
        id: Date.now() + 11,
        text: out.message || 'האישור נשמר. כתוב לי, ונתכנן יחד חזרה הדרגתית — כל שינוי יוצג לאישורך.',
        isUser: false,
        timestamp: new Date().toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' }),
      }]);
    }
    return out;
  };
  const [inputValue, setInputValue] = useState('');
  const [autoSendMsg, setAutoSendMsg] = useState(null);  // deep-analysis auto-send from the card button
  const [deepBusy, setDeepBusy] = useState(false);       // deep analysis running → immediate "working" banner
  const [isLoading, setIsLoading] = useState(false);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  // Onboarding plan generation status — controls the dedicated
  // progress bubble shown after the welcome message for new users.
  // null = not in flow, 'generating' = waiting on the LLM,
  // (Onboarding plan-gen status removed: State 1 of the new
  // architecture moved generation to OnboardingLoader, which blocks
  // the user before they reach the chat. Chat no longer needs to
  // track that flow — when the user arrives here the plan already
  // exists in the DB.)
  const messagesEndRef = useRef(null);
  const welcomeShownRef = useRef(false); // prevent duplicate welcome
  // Track in-flight chat sends so the input stays usable while a slow
  // tool-call (plan modification, ~60-120s) is processing. inFlightCount
  // shows N spinners in the loading area when the user has fired multiple
  // messages in parallel.
  const [inFlightCount, setInFlightCount] = useState(0);
  // Subset of inFlightCount: how many of the in-flight requests are
  // LIKELY plan-modification asks (per the Hebrew keyword heuristic
  // below). When > 0, a second modification request gets blocked with
  // a friendly 'I'm just arranging your plan, give me a few seconds'
  // message. Regular Q&A / info questions always fire in parallel.
  const [inFlightUpdates, setInFlightUpdates] = useState(0);
  // The first user message's send time. Drives the progress-stage text in
  // the loading bubble — 'thinking' at 0-5s, 'updating plan' at 30s+, etc.
  // Reset to null when no requests are in flight.
  const [oldestSendStartedAt, setOldestSendStartedAt] = useState(null);
  // Forces a re-render every second so the elapsed-time progress text
  // refreshes without each handleSend having to drive its own interval.
  const [, _tickRerender] = useState(0);
  useEffect(() => {
    if (inFlightCount === 0) return;
    const id = setInterval(() => _tickRerender(t => t + 1), 1000);
    return () => clearInterval(id);
  }, [inFlightCount]);

  // Deep-analysis auto-send: the insight-card button stashes a prompt in
  // localStorage and navigates here. Load it into the input, then fire it through
  // the FULL chat pipeline (splits + profile + history) — same rich result as if
  // the user typed it. handleSend reads inputValue, so we wait until it's set.
  useEffect(() => {
    if (!userId) return;
    // Suggested-question auto-send (handoff_chat_card): same mechanism as the
    // deep-analysis stash below, but WITHOUT the deep-analysis banner — this
    // is an ordinary chat question. Sent exactly once via the autoSend flow.
    let pendingQ = null;
    try { pendingQ = localStorage.getItem(`pendingQuestion_${userId}`); } catch {}
    if (pendingQ) {
      try { localStorage.removeItem(`pendingQuestion_${userId}`); } catch {}
      setInputValue(pendingQ);
      setAutoSendMsg(pendingQ);
      return;
    }
    let pending = null;
    try { pending = localStorage.getItem(`pendingAnalysis_${userId}`); } catch {}
    if (pending) {
      try { localStorage.removeItem(`pendingAnalysis_${userId}`); } catch {}
      setInputValue(pending);
      setAutoSendMsg(pending);
      // Immediate feedback: show a "deep analysis running in the background" banner the
      // moment we land here — BEFORE the auto-send fires (it waits for history to load,
      // which can be several seconds on a cold Render start). Without this the user saw a
      // blank chat and thought nothing happened (critical UX bug 2026-07-15). Cleared when
      // the analysis (or a failure) arrives.
      setDeepBusy(true);
    }
  }, [userId]);

  useEffect(() => {
    // Wait for history to finish loading before auto-sending the deep-analysis
    // message — otherwise loadChatHistory's setMessages(history) races and briefly
    // overwrites the just-added question ('didn't appear immediately').
    if (autoSendMsg && inputValue === autoSendMsg && !isLoading && historyLoaded) {
      setAutoSendMsg(null);
      handleSend();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoSendMsg, inputValue, isLoading, historyLoaded]);
  
  // ── Waiting for a background deep analysis (utils/deepAnalysisWait.js, 2026-10-24). The wait lives in
  //    sessionStorage, so leaving Coach and coming back RESUMES it — history reads only, never another /chat request
  //    or AI call. Matching = the exact message sent (it names the claimed workout) + freshness; one history request
  //    may never hang the wait (20 s timeout); polling stops (and in-flight requests abort) when Coach unmounts.
  const deepWaitRef = useRef(null);
  const stopDeepWait = () => {
    const w = deepWaitRef.current;
    if (w) { clearTimeout(w.timer); w.ctrl.abort(); }
    deepWaitRef.current = null;
  };
  const runDeepWait = (wait, firstDelayMs = 5000) => {
    stopDeepWait();
    if (!wait) return;
    const ctrl = new AbortController();
    const state = { ctrl, timer: null, wait };
    deepWaitRef.current = state;
    setDeepBusy(true);
    const finish = () => { if (deepWaitRef.current === state) deepWaitRef.current = null; setDeepBusy(false); };
    const tick = async () => {
      if (ctrl.signal.aborted) return;
      try {
        const r = await fetchWithTimeout(authFetch, `${API_BASE_URL}/chat/history?user_id=${userId}&limit=20`,
                                         { signal: ctrl.signal });
        if (ctrl.signal.aborted) return;
        if (r && r.ok) {
          const h = await r.json();
          if (ctrl.signal.aborted) return;
          const hit = findReply(h && h.messages, wait);
          if (hit) {
            clearWait(userId);
            finish();
            setMessages(prev => (prev.some(m => !m.isUser && m.text === hit.response) ? prev : [...prev, {
              id: `deep-${hit.id || Date.now()}`,
              text: hit.response,
              isUser: false,
              timestamp: new Date().toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' }),
            }]));
            return;
          }
        }
      } catch {
        if (ctrl.signal.aborted) return;            // Coach closed — the stored wait resumes on reopen
      }
      const delay = nextDelay(Date.now() - wait.sentAt);
      if (delay === null) {                          // the wait is over — this says nothing about the job itself
        clearWait(userId);
        finish();
        setMessages(prev => [...prev, {
          id: Date.now() + 8,
          _deepRefresh: true,
          isUser: false,
          timestamp: new Date().toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' }),
        }]);
        return;
      }
      state.timer = setTimeout(tick, delay);
    };
    state.timer = setTimeout(tick, firstDelayMs);
  };
  useEffect(() => () => stopDeepWait(), []);

  // ── Plan-change proposals (2026-10-25): the coach prepares a change, the card shows it exactly, and ONLY the
  //    card's "אישור השינוי" writes it (utils/planProposal.js). Polling stops when Coach closes; an open card comes
  //    back after every history load (leaving Coach and returning).
  const proposalCtrlRef = useRef(new AbortController());
  useEffect(() => () => proposalCtrlRef.current.abort(), []);
  const upsertProposal = (view) => {
    if (!view || !view.proposal_id) return;
    setMessages(prev => {
      const i = prev.findIndex(m => m._proposal && m._proposal.proposal_id === view.proposal_id);
      if (i >= 0) {
        const next = prev.slice();
        next[i] = { ...prev[i], _proposal: { ...prev[i]._proposal, ...view } };
        return next;
      }
      return [...prev, { id: `proposal-${view.proposal_id}`, _proposal: view, isUser: false,
        timestamp: new Date().toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' }) }];
    });
  };
  const trackProposal = (view) => {
    upsertProposal(view);
    if (view.status === 'processing') {
      watchProposal({ fetcher: authFetch, uid: userId, id: view.proposal_id, onUpdate: upsertProposal,
        signal: proposalCtrlRef.current.signal });
    }
  };
  const resumeOpenProposal = async () => {
    try {
      const view = await fetchOpenProposal(authFetch, userId);
      // Only a card that is still open comes back: an expired one (the backend keeps it 'pending' and reports
      // 'expired' when viewed) used to reappear at the end of every later conversation (2026-10-27).
      if (!proposalCtrlRef.current.signal.aborted && view && view.proposal_id
          && RESUMABLE_PROPOSAL_STATUSES.includes(view.status)) trackProposal(view);
    } catch { /* no card — the chat works as before */ }
  };
  const handleApproveProposal = async (id) => {
    upsertProposal({ proposal_id: id, status: 'applying' });
    try {
      const out = await approveProposal(authFetch, userId, id);
      upsertProposal({ proposal_id: id, status: out.status, message: out.message });
      if (out.status === 'applied') {
        try {
          window.dispatchEvent(new CustomEvent('plansUpdated', { detail: { source: 'chat-proposal', plan_id: out.plan_id } }));
        } catch {}
      }
    } catch (e) {
      upsertProposal({ proposal_id: id, status: e && e.status === 404 ? 'failed' : 'approve_error',
        message: e && e.status === 404 ? 'ההצעה לא נמצאה — התכנית לא השתנתה.' : undefined,
        error: 'לא הצלחתי לשמור כרגע — התכנית לא השתנתה. נסה שוב.' });
    }
  };
  const handleRejectProposal = async (id) => {
    upsertProposal({ proposal_id: id, status: 'applying' });
    try {
      const out = await rejectProposal(authFetch, userId, id);
      upsertProposal({ proposal_id: id, status: out.status, message: out.message });
    } catch {
      upsertProposal({ proposal_id: id, status: 'approve_error', error: 'לא הצלחתי לבטל כרגע — נסה שוב.' });
    }
  };
  // Coach reopened while an analysis was still being written: resume reading history (check right away).
  useEffect(() => {
    if (!userId || !historyLoaded || deepWaitRef.current) return;
    const w = loadWait(userId);
    if (w) runDeepWait(w, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, historyLoaded]);

  // Message limit & Trial state
  const [dailyMessageCount, setDailyMessageCount] = useState(0);
  const [dailyLimit, setDailyLimit] = useState(10);
  const [limitReached, setLimitReached] = useState(false);
  const [trialDaysLeft, setTrialDaysLeft] = useState(7);
  const [userPlan, setUserPlan] = useState('trial');
  const [isTrialExpired, setIsTrialExpired] = useState(false);
  const [resetsAt, setResetsAt] = useState(null);       // the server's reset time (/usage, /chat) — the card's countdown
  const [urgentOpen, setUrgentOpen] = useState(false);  // limit reached: one message opened for something urgent

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // ✅ FIX: Load chat history only when userId is available.
  // Reverted the onboarding-skip guard because it left users blank-
  // screened when a stale localStorage.showOnboardingChat_* flag
  // persisted from an interrupted onboarding (the welcome flow refused
  // to re-run, AND the history was skipped → empty messages). Now
  // history always loads; the placeholder welcome below handles the
  // initial blank-screen problem for genuine new users.
  useEffect(() => {
    if (userId && !historyLoaded) {
      loadChatHistory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, historyLoaded]);

  // Load message count from SERVER on mount
  useEffect(() => {
    if (userId) {
      loadDailyMessageCount();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  // Determine if we should show onboarding welcome — either from prop or localStorage
  const shouldShowOnboardingWelcome = fromOnboarding ||
    (userId && localStorage.getItem(`showOnboardingChat_${userId}`) === 'true');

  // Show personalized welcome after onboarding
  useEffect(() => {
    if (!shouldShowOnboardingWelcome || !userId) return;
    if (welcomeShownRef.current) return; // prevent double run in same session
    // Also check localStorage guard to prevent re-run after remount
    if (localStorage.getItem(`welcomeRunning_${userId}`)) return;
    // 🔧 Wait for chat history to load before deciding to run welcome.
    // If the user already has a welcome in DB (e.g. they refreshed after a
    // first successful onboarding), running it again would create a duplicate.
    if (!historyLoaded) return;
    // Skip if welcome is already in history (covers the case where the
    // onboarding-completion flag survived but the welcome was saved on
    // a previous attempt — common when Render cold-starts and the user
    // refreshed).
    const welcomeAlreadyInHistory = messages.some(
      (m) => !m.isUser && typeof m.text === 'string' && m.text.includes('איזה כיף שהצטרפת')
    );
    if (welcomeAlreadyInHistory) {
      welcomeShownRef.current = true;
      localStorage.removeItem(`showOnboardingChat_${userId}`);
      return;
    }
    localStorage.setItem(`welcomeRunning_${userId}`, '1');
    welcomeShownRef.current = true;
    const API = process.env.REACT_APP_API_URL || 'https://claude-coach-backend-1.onrender.com';

    // Immediate placeholder so the user doesn't see a blank screen for
    // the ~500ms–2s it takes to fetch profile + races + cycling profile.
    // Will be REPLACED (same id) once the personalized text is built.
    setMessages([{
      id: 'onboarding-welcome',
      text: 'היי! 🏃\n\nאני בודק את הפרטים שלך ומכין את התוכנית הראשונה — רגע אחד...',
      isUser: false,
      isOnboardingWelcome: true,
      timestamp: new Date().toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' })
    }]);

    // Fetch profile for personalisation
    const buildWelcome = async () => {
      let name = '', goal = '', injury = '', sports = [];
      try {
        const r = await authFetch(`${API}/profile?user_id=${userId}`);
        if (r.ok) {
          const p = await r.json();
          name = p.name || '';
          goal = p.goal_distance || '';
          injury = p.injury_prone_areas || '';
        }
        const cr = await authFetch(`${API}/profile/cycling/${userId}`);
        if (cr.ok) {
          const cp = await cr.json();
          sports = (cp.sports_active || 'running').split(',');
        }
      } catch {}

      const goalLabel = { '5': '5K', '10': '10K', '21.1': 'חצי מרתון', '42.2': 'מרתון', 'half': 'חצי מרתון', 'marathon': 'מרתון' }[goal] || goal;
      const sportIcon = sports.includes('cycling') && !sports.includes('running') ? '🚴‍♂️' : '🏃‍♂️';

      // Check for upcoming races
      let nextRace = null;
      try {
        const rr = await authFetch(`${API}/races?user_id=${userId}`);
        if (rr.ok) {
          const races = await rr.json();
          const today = new Date().toISOString().split('T')[0];
          const upcoming = (races.races || races || []).filter(r => r.race_date >= today).sort((a, b) => a.race_date > b.race_date ? 1 : -1);
          if (upcoming.length > 0) nextRace = upcoming[0];
        }
      } catch {}

      const raceLabel = nextRace
        ? `${nextRace.race_name || ''} (${nextRace.race_date?.slice(0,10) || ''})`.trim()
        : goalLabel;

      let text = `היי ${name || 'אתלט'}! איזה כיף שהצטרפת! ${sportIcon}\n\nניתחתי את הנתונים שלך.`;
      if (raceLabel) text += ` ראיתי שהיעד שלך הוא **${raceLabel}**`;
      if (injury) text += ` ושיש לך **${injury}** — אל דאגה, לקחתי את זה בחשבון בבניית קצבי האימון ועומס הריצות הארוכות כדי שלא נפצע`;
      // The plan is already saved by OnboardingLoader (State 1 of the
      // new architecture). Reference it in past tense and point the
      // user at the WeeklyPlan tab — that's where the artefact lives.
      text += `.\n\nבניתי לך תוכנית אימונים לשבוע הנוכחי ולשבוע הבא — היא מחכה לך בטאב "תוכנית שבועית". כאן אני זמין לכל שאלה 💬`;

      const welcomeMsg = {
        id: 'onboarding-welcome',
        text,
        isUser: false,
        isOnboardingWelcome: true,
        timestamp: new Date().toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' })
      };
      setMessages(prev => [welcomeMsg, ...prev.filter(m => m.id !== 'onboarding-welcome')]);
      // Save welcome message to chat history (same retention as regular messages)
      authFetch(`${API}/chat/save-system-message?user_id=${userId}`, {   // the text in the body, never the URL
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ message: text }),
      }).catch(() => {});
      localStorage.removeItem(`showOnboardingChat_${userId}`);
      localStorage.removeItem(`welcomeRunning_${userId}`);
      // Plan-gen flow is owned by OnboardingLoader now — Chat no longer
      // fires V2 generation. The bubble / status state / poll were all
      // tied to that flow and have been removed.
    };
    buildWelcome();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldShowOnboardingWelcome, userId, historyLoaded]);

  // ============ MESSAGE LIMIT FUNCTIONS (SERVER-BASED) ============
  
  const loadDailyMessageCount = async () => {
    try {
      const response = await authFetch(`${API_BASE_URL}/usage/${userId}`);
      if (response.ok) {
        const data = await response.json();
        setDailyMessageCount(data.messages_used || 0);
        setDailyLimit(data.daily_limit || 10);
        setLimitReached(!data.can_send);
        setTrialDaysLeft(data.trial_days_left || 0);
        setUserPlan(data.plan || 'trial');
        setIsTrialExpired(data.is_trial_expired || false);
        setResetsAt(data.resets_at || null);
        console.log(`📊 Usage: ${data.messages_used}/${data.daily_limit}, Plan: ${data.plan}, Trial days: ${data.trial_days_left}`);
      }
    } catch (error) {
      console.error('Error loading usage from server:', error);
      // Fallback: allow messages if server is down
      setLimitReached(false);
    }
  };

  const updateUsageFromResponse = (responseData) => {
    // עדכון מידע מתשובת הצ'אט
    const limit = responseData.daily_limit || dailyLimit;
    if (responseData.daily_limit) setDailyLimit(responseData.daily_limit);
    if (responseData.resets_at) setResetsAt(responseData.resets_at);
    if (responseData.messages_remaining !== undefined) {
      setDailyMessageCount(limit - responseData.messages_remaining);
      // the day's last message was answered -> the limit card (the server decides every later message)
      if (responseData.messages_remaining <= 0) setLimitReached(true);
    }
    if (responseData.trial_days_left !== undefined) {
      setTrialDaysLeft(responseData.trial_days_left);
    }
    if (responseData.plan) {
      setUserPlan(responseData.plan);
    }
    if (responseData.trial_expired) {
      setIsTrialExpired(true);
    }
    if (responseData.limit_reached) {
      setLimitReached(true);
    }
  };

  // =================================================

  const loadChatHistory = async () => {
    console.log('📥 Loading chat history for user:', userId);
    // Hydrate from cache FIRST so the user sees their last-known
    // history instead of a blank chat while Render flips deploys.
    // The cache is overwritten on every successful fetch below.
    if (userId) {
      try {
        const cached = localStorage.getItem(`chat_history_${userId}`);
        if (cached) {
          const arr = JSON.parse(cached);
          if (Array.isArray(arr) && arr.length > 0) {
            setMessages(arr);
            setHistoryLoaded(true);   // unblock dependent effects immediately
          }
        }
      } catch {}
    }
    // Wrap the fetch with retry — Render free tier cold-start can take
    // 30-60s, and the standard 'Failed to fetch' error is opaque to the
    // user. Try up to 5 times with exponential backoff (2s, 4s, 8s, 16s).
    const MAX_RETRIES = 5;
    let lastErr = null;
    let response = null;
    for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
      try {
        if (attempt > 0) {
          const waitMs = Math.min(2000 * Math.pow(2, attempt - 1), 16000);
          await new Promise(r => setTimeout(r, waitMs));
          console.log(`📥 Retrying chat history fetch (${attempt + 1}/${MAX_RETRIES}) after ${waitMs}ms...`);
        }
        // authFetch attaches Authorization: Bearer <Firebase ID token>
        // for the privacy-by-design rollout — backend's require_user_token
        // dependency expects it (shadow mode today, enforce later).
        response = await authFetch(`${API_BASE_URL}/chat/history?user_id=${userId}&limit=100`, {
          cache: 'no-store',
          headers: { 'Cache-Control': 'no-cache' },
        });
        break; // success — proceed to processing
      } catch (err) {
        lastErr = err;
        console.warn(`📥 chat history attempt ${attempt + 1} failed:`, err.message);
      }
    }
    if (!response) {
      console.error('📥 chat history: all retries failed:', lastErr);
      // Keep any in-memory welcome message visible; don't blow up the UI.
      setMessages(prev => prev.some(m => m.isOnboardingWelcome) ? prev : []);
      setHistoryLoaded(true);
      return;
    }
    try {
      
      if (response.ok) {
        const data = await response.json();
        console.log(`📥 API returned ${data.count} messages`);
        
        if (data.messages && data.messages.length > 0) {
          // Diagnostic log to verify what's coming back from the server
          // (was helping debug an apparent "messages disappeared" report —
          // safe to remove later once stable).
          console.log('📥 Chat history payload:', data.messages.map(m => ({
            id: m.id,
            has_message: !!(m.message && m.message.length > 0),
            message_len: (m.message || '').length,
            has_response: !!(m.response && m.response.length > 0),
            response_len: (m.response || '').length,
          })));
          // Robust timestamp parser: assumes UTC when the backend
          // sends an ISO string without timezone info. Without this,
          // `new Date(...)` interprets such strings as local time and
          // chat timestamps display off by the user's TZ offset
          // (3 hours wrong for Israel in DST).
          const parseTsUtc = (ts) => {
            if (!ts) return '';
            const s = String(ts);
            const hasTz = /[Zz]$|[+\-]\d\d:?\d\d$/.test(s);
            return new Date(hasTz ? s : s + 'Z').toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' });
          };
          const historyMessages = [];
          data.messages.forEach((msg) => {
            // Push the user-side bubble for EVERY row that has any message
            // content (even whitespace). The previous trim()-based filter
            // was too aggressive and could hide real messages — until we
            // root-cause the missing-messages report, we lean on the
            // permissive side. Only completely-empty-string AND null
            // messages get suppressed (those come from
            // /chat/save-system-message intentionally).
            const hasUserContent = msg.message !== null && msg.message !== '' && msg.message !== undefined;
            if (hasUserContent) {
              historyMessages.push({
                id: `history-user-${msg.id}`,
                text: msg.message,
                isUser: true,
                timestamp: parseTsUtc(msg.timestamp)
              });
            }
            const hasCoachContent = msg.response !== null && msg.response !== '' && msg.response !== undefined;
            if (hasCoachContent) {
              historyMessages.push({
                id: `history-coach-${msg.id}`,
                text: msg.response,
                isUser: false,
                timestamp: parseTsUtc(msg.timestamp)
              });
            }
          });
          setMessages(historyMessages);
          // Persist for the next mount — hydration at the top of
          // loadChatHistory reads this. Best-effort; localStorage can
          // throw on quota and we don't want to break the chat path.
          try { localStorage.setItem(`chat_history_${userId}`, JSON.stringify(historyMessages)); } catch {}
          console.log(`✅ Loaded ${historyMessages.length} messages into UI`);
        } else {
          console.log('📥 No history found');
          // Don't wipe messages set by the welcome flow during onboarding.
          // Race: welcome flow does setMessages([welcomeMsg]) in-memory and
          // fires the save POST. If loadChatHistory's fetch lands at the
          // backend before the save commits, history is empty here and
          // setMessages([]) would erase the welcome — leaving a blank chat.
          setMessages(prev => prev.some(m => m.isOnboardingWelcome) ? prev : []);
        }
        setHistoryLoaded(true);
        resumeOpenProposal();
      }
    } catch (error) {
      console.error('Failed to load chat history:', error);
      // Same race protection as above — also covers Render cold-start
      // 'Failed to fetch' errors that previously wiped the welcome.
      setMessages(prev => prev.some(m => m.isOnboardingWelcome) ? prev : []);
      setHistoryLoaded(true);
    }
  };

  // Cheap Hebrew keyword heuristic for 'this message is asking to modify
  // the plan'. Used only to enforce the no-concurrent-updates rule —
  // false positives just delay an info request, false negatives let
  // two updates race (rare; Gemini Flash still picks one and the tool
  // backend serializes the orchestrator call). Pure regex on purpose:
  // sending the message to the backend just to detect intent would
  // defeat the point of blocking BEFORE the request fires.
  const _looksLikePlanUpdate = (txt) => {
    if (!txt) return false;
    const s = String(txt);
    // Verbs that almost always mean 'change my plan'.
    const verbs = /(תקצר|תאריך|תאריך\s+את|תעדכן|תשנה|תזיז|תוסיף|תוריד|החלף|ערוך|הוסיף|להוסיף|לעדכן|לשנות|להזיז|לקצר|להאריך|להחליף|לערוך)/;
    if (verbs.test(s)) return true;
    // Phrases that are clearly update requests even without a clear verb.
    if (/אימון\s+(של|ל)?(יום|ב)/.test(s) && /(שנה|זוז|הזז|במקום|במקום\s+ל|זאת)/.test(s)) return true;
    return false;
  };

  // Detect "user confirmed an update proposal". Backend system prompt
  // now requires the LLM to ask 'אני עומד לעדכן... לאשר?' BEFORE calling
  // the tool. So the actual plan modification happens on the user's
  // FOLLOWUP turn, not the first verb-rich message. We fire the global
  // banner (plan-regen-start) on this followup so it stays visible
  // after the user navigates away from the chat — matching the
  // Profile / Races flow. Short approval words + a recent assistant
  // bubble containing 'לאשר?' is the signal.
  const _isConfirmationReply = (txt, msgs) => {
    if (!txt || !msgs || !msgs.length) return false;
    const short = String(txt).trim();
    if (short.length > 30) return false;
    const approvalRe = /^(כן|אישור|תאשר|אשר|אשרו|ok|אוקיי|אוקי|תעשה|תבצע|בסדר|נכון|מאשר)\b/i;
    if (!approvalRe.test(short)) return false;
    // Look back at recent assistant messages for the 'לאשר?' marker.
    for (let i = msgs.length - 1; i >= Math.max(0, msgs.length - 6); i--) {
      const m = msgs[i];
      const txt2 = m?.text || '';
      const isAssistant = m && m.isUser === false;
      if (!isAssistant) continue;
      if (/לאשר\??/.test(txt2) && (
          /אני עומד לעדכן/.test(txt2) ||
          /מתכוון לעדכן/.test(txt2) ||
          /אעדכן את התכנית/.test(txt2))) {
        return true;
      }
    }
    return false;
  };

  const handleSend = async () => {
    if (!inputValue.trim()) return;

    // Safety: only one PLAN-MODIFICATION request can be in flight at a
    // time. Regular Q&A still fires in parallel (the user explicitly
    // asked for this: 'block only plan-update requests'). The slow
    // path runs 60-180s and a second modification mid-way risks
    // double-modifying the plan or losing the second update behind a
    // stale DB read.
    const _isUpdateAsk = _looksLikePlanUpdate(inputValue);
    if (_isUpdateAsk && inFlightUpdates > 0) {
      setMessages((prev) => [
        ...prev,
        {
          id: Date.now() - 1,
          text: inputValue,
          isUser: true,
          timestamp: new Date().toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' }),
        },
        {
          id: Date.now(),
          text: 'אני בדיוק מסדר לך את התוכנית ברקע ברגע זה, תן לי כמה שניות לסיים ואז נוכל לבצע שינויים נוספים!',
          isUser: false,
          timestamp: new Date().toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' }),
        },
      ]);
      setInputValue('');
      return;
    }

    // Limit reached (2026-10-30): the message still goes to the server — its emergency check runs BEFORE the limit
    // (a red flag gets the safety reply, not counted); anything else gets the limit reply (no model, not counted).
    // The client never decides alone that a message is not urgent. The input is hidden behind the limit card; it
    // opens for one urgent message (urgentOpen) or carries an auto-sent question.
    setUrgentOpen(false);

    const userMessage = {
      id: Date.now(),
      text: inputValue,
      isUser: true,
      timestamp: new Date().toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' })
    };

    setMessages((prev) => [...prev, userMessage]);
    const messageToSend = inputValue;
    setInputValue('');
    setIsLoading(true);
    setInFlightCount(c => c + 1);
    if (_isUpdateAsk) setInFlightUpdates(c => c + 1);

    // Global banner: this turn is the user confirming an update the
    // LLM proposed last turn ('לאשר?' → 'כן'). The actual tool call
    // happens server-side now; dispatch plan-regen-start so the
    // banner in App.js shows on every screen, surviving navigation
    // out of chat. Done event is dispatched after the response.
    const _cardOpen = messages.some(m => m._proposal && ['processing', 'pending', 'approve_error'].includes(m._proposal.status));
    const _willActuallyUpdate = !_cardOpen && _isConfirmationReply(inputValue, messages);
    if (_willActuallyUpdate) {
      try {
        window.dispatchEvent(new CustomEvent('plan-regen-start', {
          detail: { changedFields: ['עדכון תכנית מהצ\'אט'] },
        }));
      } catch {}
    }
    // Stamp the start time only if no other request is already in
    // flight — the loading bubble's progress text follows the OLDEST
    // pending request so the user sees the worst-case ETA.
    const _sendStart = Date.now();
    setOldestSendStartedAt(prev => prev ?? _sendStart);

    // Read-only refactor: chat never auto-routes to plan generation. Plans
    // are created through the dedicated "תכנית שבועית" tab button. Chat
    // returns text answers only — analysis, advice, structured Q&A.

    // Chat is text-only — no special "preparing a plan" message. The
    // standard "חושב..." loading indicator covers the wait.

    // State 3: when the chat invokes the update_active_plan tool, the
    // request balloons to two Gemini Flash calls + one Gemini Pro call
    // (revise). Total worst-case ≈ 180s. Without an explicit timeout
    // the fetch hangs silently if anything in the chain stalls and the
    // user sees nothing — no error, no spinner change. Cap at 240s
    // and surface a clear message on timeout.
    const _chatCtrl = new AbortController();
    const _chatTimer = setTimeout(() => _chatCtrl.abort('chat-timeout'), 240_000);
    try {
      // 2026-10-27 (pilot privacy): the text goes in the JSON body — the URL (kept by access logs) never carries it
      const params = new URLSearchParams({
        user_id: userId,
        include_recent_runs: 'true'
      });
      const response = await authFetch(
        `${API_BASE_URL}/chat?${params.toString()}`,
        { method: 'POST', signal: _chatCtrl.signal, headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ message: messageToSend }) },
      );

      // 429 = a limit -> the limit card (the input area). 403 is NOT the daily limit (2026-10-30): pilot access /
      // identity — its own line, never the quota card.
      if (response.status === 429) {
        setLimitReached(true);
        setMessages(prev => [...prev, { id: Date.now() + 1, _limitNote: true, isUser: false, timestamp: '' }]);
        setDeepBusy(false);
        if (_willActuallyUpdate) { try { window.dispatchEvent(new CustomEvent('plan-regen-done', { detail: { success: true } })); } catch {} }
        return;
      }
      if (response.status === 403) {
        const _detail = await response.json().then(b => b && b.detail).catch(() => null);
        setMessages(prev => [...prev, {
          id: Date.now() + 1,
          text: _detail && _detail.error === 'pilot_access_required'
            ? 'הצ\'אט עדיין לא פתוח בחשבון שלך — הגישה לפיילוט ממתינה לאישור.'
            : 'אין הרשאה לשלוח את ההודעה. נסה להתנתק ולהתחבר מחדש.',
          isUser: false,
          timestamp: new Date().toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' }),
        }]);
        setDeepBusy(false);
        if (_willActuallyUpdate) { try { window.dispatchEvent(new CustomEvent('plan-regen-done', { detail: { success: true } })); } catch {} }
        return;
      }

      if (!response.ok) {
        // Surface the status code so the user (or support) knows whether
        // to retry now, wait, or report. Silent throw → silent failure.
        const _body = await response.text().catch(() => '');
        console.error(`Chat POST failed: ${response.status} ${response.statusText}`, _body.slice(0, 200));
        throw new Error(`chat-${response.status}`);
      }

      const data = await response.json();

      // עדכון מידע על שימוש מתשובת השרת
      updateUsageFromResponse(data);

      // The daily limit (HTTP 200 + limit_reached, 2026-10-30): the limit card takes the input's place at once — the
      // server's one-line text is not shown as a coach reply; a short note says the message did not reach the coach.
      if (data.limit_reached) {
        setDailyMessageCount(data.daily_limit || dailyLimit);
        setMessages(prev => [...prev, { id: Date.now() + 1, _limitNote: true, isUser: false, timestamp: '' }]);
        setDeepBusy(false);
        if (_willActuallyUpdate) { try { window.dispatchEvent(new CustomEvent('plan-regen-done', { detail: { success: true } })); } catch {} }
        return;
      }

      // Append the chat reply (text-only) + optional CTA cards.
      // Three possible CTA payloads, can appear together:
      //   • _ambiguousRacesCTA: user's mention matches 2+ DB races
      //     → list of buttons, one per option (disambiguation)
      //   • _missingRaceCTA: user mentioned a race not in DB
      //     → button "הוסף מרוץ חדש" navigates to Races with prefill
      //   • _planCTA: user asked for a plan AND we have an anchor
      //     (matched race / nearest race / profile goal_distance)
      setMessages((prev) => {
        const reply = {
          id: Date.now() + 1,
          text: data.response,
          isUser: false,
          timestamp: new Date().toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' })
        };
        const extras = [];
        if (data.ambiguous_races_cta && Array.isArray(data.ambiguous_races_cta.options) && data.ambiguous_races_cta.options.length > 0) {
          extras.push({
            id: Date.now() + 2,
            _ambiguousRacesCTA: data.ambiguous_races_cta,
            isUser: false,
            timestamp: new Date().toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' })
          });
        }
        if (data.missing_race_cta && data.missing_race_cta.race_name) {
          extras.push({
            id: Date.now() + 3,
            _missingRaceCTA: data.missing_race_cta,
            isUser: false,
            timestamp: new Date().toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' })
          });
        }
        if (data.plan_cta) {
          extras.push({
            id: Date.now() + 4,
            _planCTA: data.plan_cta,
            isUser: false,
            timestamp: new Date().toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' })
          });
        }
        // 2026-10-27: the coach suggests keeping / clearing the injury in the profile — a card that asks first
        if (data.injury_state_offer && (data.injury_state_offer.suggest === 'pain' || data.injury_state_offer.suggest === 'ok')) {
          extras.push({ id: Date.now() + 5, _injuryOffer: data.injury_state_offer, isUser: false });
        }
        return [...prev, reply, ...extras];
      });

      // Deep analysis runs as a BACKGROUND job (it's too heavy to run inside the request —
      // it was killing the worker → stuck spinner). The backend returned an 'analysing…'
      // placeholder; the real analysis is generated in the background and SAVED to history.
      // Poll until it appears, then append it as a coach bubble. (coach spec 2026-07-13)
      if (data && data.deep_analysis_pending) {
        // The reply is saved to history under this exact message (utils/deepAnalysisWait.js).
        runDeepWait(saveWait(userId, messageToSend, _sendStart) || { message: messageToSend, sentAt: _sendStart });
      } else if (!deepWaitRef.current) {
        // No background job on this response → clear the deep-analysis banner if it was up.
        setDeepBusy(false);
      }

      // State 3: when the backend's tool-call modified an active plan,
      // tell WeeklyPlan to refetch so the dashboard reflects the new
      // workouts without a manual refresh. WeeklyPlan already listens
      // for the 'plansUpdated' window event (registered in its mount
      // effect); we just need to dispatch it. Wrapped in a
      // setTimeout so the user's chat bubble paints first.
      if (data && data.plan_proposal_id) {
        trackProposal({ proposal_id: data.plan_proposal_id, status: 'processing' });
      }
      if (data && data.safety_hold) safety.setFromReply(data.safety_hold);
      if (data && data.plan_update_job_id) {
        // The edit was QUEUED, not written (backend flag CHAT_REVISE_JOB_STATUS): report only the
        // job's final outcome — success + plan refresh only once the write committed.
        watchPlanUpdateJob({
          jobId: data.plan_update_job_id,
          fetchJob: async (id) => {
            const r = await authFetch(
              `${API_BASE_URL}/plan-status/${encodeURIComponent(id)}?user_id=${encodeURIComponent(userId)}`);
            return { status: r.status, body: r.ok ? await r.json() : null };
          },
          onMessage: (text) => setMessages(prev => [...prev, {
            id: Date.now() + 9,
            text,
            isUser: false,
            timestamp: new Date().toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' }),
          }]),
          onPlansUpdated: (planId) => {
            try {
              window.dispatchEvent(new CustomEvent('plansUpdated', {
                detail: { source: 'chat-tool-call', plan_id: planId },
              }));
            } catch {}
          },
          onBanner: (detail) => {
            try { window.dispatchEvent(new CustomEvent('plan-regen-done', { detail })); } catch {}
          },
        });
      } else if (data && data.plan_updated) {
        console.log('🛠️ [chat] plan was modified via tool-call:', data.updated_plan_id);
        setTimeout(() => {
          try {
            window.dispatchEvent(new CustomEvent('plansUpdated', {
              detail: { source: 'chat-tool-call', plan_id: data.updated_plan_id },
            }));
          } catch (e) {
            console.warn('plansUpdated dispatch failed:', e);
          }
        }, 200);
        // Close the global banner with success. If _willActuallyUpdate
        // was false but plan_updated came back true anyway (LLM skipped
        // the confirmation despite the system prompt), still fire done
        // so the banner doesn't get stuck open from any earlier start.
        try {
          window.dispatchEvent(new CustomEvent('plan-regen-done', {
            detail: { success: true },
          }));
        } catch {}
      } else if (_willActuallyUpdate) {
        // We started the banner because this looked like an approval
        // turn, but the response didn't carry plan_updated — likely
        // the LLM re-asked or refused. Close the banner gracefully
        // with success=true so the user doesn't see a red error for
        // a no-op turn.
        try {
          window.dispatchEvent(new CustomEvent('plan-regen-done', {
            detail: { success: true },
          }));
        } catch {}
      }

    } catch (error) {
      console.error('Chat error:', error);
      setDeepBusy(false);
      setMessages((prev) => {
        const isTimeout = error.name === 'AbortError' || (error.message || '').includes('chat-timeout');
        const isHttpErr = (error.message || '').startsWith('chat-');
        return [...prev, {
          id: Date.now() + 1,
          text: isTimeout
            ? '⏳ הבקשה לקחה יותר מ-4 דקות. כנראה השרת בעומס — נסה שוב בעוד דקה.'
            : isHttpErr
            ? `❌ שגיאה בתקשורת (${error.message.replace('chat-', 'HTTP ')}). נסה שוב.`
            : '❌ שגיאה בתקשורת. נסה שוב.',
          isUser: false,
          timestamp: new Date().toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' })
        }];
      });
      // Resilience: the reply may have been generated and SAVED server-side even
      // though the HTTP response was lost (502 / timeout when Render restarts mid-
      // request). Reload history shortly after — if the reply landed, it replaces
      // the error bubble so the user never needs a manual F5. No-op if nothing saved.
      setTimeout(() => { try { loadChatHistory(); } catch {} }, 4000);
      // Close any global banner we opened — fail loudly here since
      // the request actually errored, not just refused.
      if (_willActuallyUpdate) {
        try {
          window.dispatchEvent(new CustomEvent('plan-regen-done', {
            detail: { success: false, error: String(error?.message || error) },
          }));
        } catch {}
      }
    } finally {
      clearTimeout(_chatTimer);
      setInFlightCount(c => {
        const next = Math.max(0, c - 1);
        // When the queue drains, reset the elapsed-time stamp so the
        // next send starts the progress text from 0.
        if (next === 0) setOldestSendStartedAt(null);
        return next;
      });
      if (_isUpdateAsk) setInFlightUpdates(c => Math.max(0, c - 1));
      setIsLoading(false);
    }
  };

  // Parse AI message body and pull out CoachNote callouts.
  // Supported markers (AI may emit these via the chat system prompt):
  //   [[coach: ...]]   → tactical CoachNote (amber)
  //   [[warning: ...]] → warning CoachNote (red)
  //   [[info: ...]]    → info CoachNote (cyan)
  // Plain text outside the markers is rendered as before with <br/>.
  const formatMessage = (text) => {
    if (!text) return null;
    const re = /\[\[(coach|warning|info)\s*:\s*([\s\S]+?)\]\]/g;
    const parts = [];
    let lastIdx = 0;
    let match;
    let key = 0;
    while ((match = re.exec(text)) !== null) {
      if (match.index > lastIdx) {
        parts.push({ type: 'text', value: text.slice(lastIdx, match.index), key: key++ });
      }
      const variant = match[1] === 'warning' ? 'warning' : match[1] === 'info' ? 'info' : 'tactical';
      parts.push({ type: 'note', variant, value: match[2].trim(), key: key++ });
      lastIdx = re.lastIndex;
    }
    if (lastIdx < text.length) {
      parts.push({ type: 'text', value: text.slice(lastIdx), key: key++ });
    }

    return parts.map((p) => {
      if (p.type === 'note') {
        return (
          <CoachNote key={p.key} variant={p.variant} compact className="my-2">
            {p.value}
          </CoachNote>
        );
      }
      // Plain-text segment — preserve newlines as <br/> like before; **bold** renders as bold (display only).
      return p.value.split('\n').map((line, i, arr) => (
        <span key={`${p.key}-${i}`}>
          {line.split(/\*\*(.+?)\*\*/g).map((seg, j) => (j % 2 ? <strong key={j} className="font-bold">{seg}</strong> : seg))}
          {i < arr.length - 1 && <br />}
        </span>
      ));
    });
  };

  const remainingMessages = Math.max(0, dailyLimit - dailyMessageCount);

  return (
    <div className="min-h-screen bg-bg">
    <div className="max-w-lg mx-auto min-h-screen flex flex-col" dir="rtl">

      {/* Header — Graphite: "מאמן" 28/800; under it "המאמן האישי שלך" and the day's counter "<used>/<limit> הודעות
          היום" (2026-10-30 limit design: the number bright + bold when 2 or fewer are left). No pills, no emoji. */}
      <header className="sticky top-0 z-20 bg-bg border-b border-border">
        <div className="px-4 pt-3 pb-2">
          <div className="flex items-center justify-between gap-3">
            <h1 className="text-[28px] font-extrabold leading-tight text-text">מאמן</h1>
            <button
              type="button"
              onClick={onBack}
              aria-label="למסך הבית"
              className="w-11 h-11 rounded-full border border-border flex items-center justify-center text-text-body"
            >
              <House size={20} weight="bold" />
            </button>
          </div>
          <div className="mt-0.5 flex items-center justify-between gap-3">
            <span className="text-[13px] text-text-muted">המאמן האישי שלך</span>
            {isTrialExpired ? (
              <span className="text-[13px] text-text-muted">תקופת הניסיון הסתיימה</span>
            ) : (
              <QuotaCounter used={limitReached ? dailyLimit : Math.min(dailyMessageCount, dailyLimit)} limit={dailyLimit} />
            )}
          </div>
        </div>
      </header>

      {/* Profile Warning */}
      {profileComplete === false && (
        <div className="mx-4 mt-2 p-3 rounded-xl bg-pressed border border-border">
          <p className="text-text-body text-sm text-center">
            השלם את הפרופיל לקבלת ניתוחים מדויקים יותר
          </p>
        </div>
      )}

      {/* Trial Expired Banner */}
      {isTrialExpired && (
        <div className="mx-4 mt-2 p-4 rounded-xl bg-surface border border-border">
          <div className="flex items-start gap-3">
            <div className="flex-1">
              <p className="text-text font-bold text-lg">תקופת הניסיון הסתיימה!</p>
              <p className="text-text-body text-sm mt-1">נהנית מ-7 ימי Trial? המשך להתאמן עם Pro!</p>
              <div className="mt-3 flex flex-wrap gap-2">
                <div className="flex items-center gap-1 text-xs text-text-muted">
                  15 הודעות ביום
                </div>
                <div className="flex items-center gap-1 text-xs text-text-muted">
                  תכניות מותאמות
                </div>
                <div className="flex items-center gap-1 text-xs text-text-muted">
                  ניתוח ריצות
                </div>
              </div>
              <button className="mt-3 px-4 py-2 rounded-lg btn-primary text-sm transition-all">
                שדרג ל-Pro - $10/חודש
              </button>
            </div>
          </div>
        </div>
      )}

      {/* The daily limit: the card takes the input area's place (below); the history stays readable at 45% */}

      {/* (Sticky onboarding plan-gen banner removed: OnboardingLoader
          owns plan generation now and the user only reaches Chat after
          the plan is already in the DB. The three banners — generating
          / success / error — and the _planGenStatus message bubbles
          that lived in the messages list went with it.) */}

      {/* Safety hold: no training / no plan change until a medical release (server-enforced) */}
      <SafetyHoldBanner hold={safety.hold} onRelease={handleSafetyRelease} today={israelToday} />

      {/* Messages */}
      <main className={`flex-1 overflow-y-auto ${limitReached ? 'opacity-[0.45]' : ''}`} data-testid="chat-history">
        <div className="container mx-auto px-4 py-4 max-w-lg">
          <div className="space-y-4">
            {messages.map((message, idx) => (
              <div key={message.id} className={`flex ${message.isUser ? 'justify-start' : 'justify-end'}`}>
                {message._proposal ? (
                  <PlanProposalCard
                    proposal={message._proposal}
                    onApprove={() => handleApproveProposal(message._proposal.proposal_id)}
                    onReject={() => handleRejectProposal(message._proposal.proposal_id)}
                    onOpenRaces={onNavigateToRaces ? () => onNavigateToRaces() : undefined}
                    onOpenProfile={onNavigateToProfile ? (section) => onNavigateToProfile(section) : undefined}
                    coachText={coachTextBefore(messages, idx)}
                  />
                ) : message._injuryOffer ? (
                  <InjuryStateOffer offer={message._injuryOffer}
                                    onOpenProfile={onNavigateToProfile ? () => onNavigateToProfile('health') : undefined} />
                ) : message._limitCard ? (
                  null /* an older inline limit card in the cached history — the card is now the input area */
                ) : message._limitNote ? (
                  <p className="m-0 max-w-[85%] text-[13px] text-text-muted" data-testid="limit-note">
                    ההודעה לא הגיעה למאמן — המכסה היומית הסתיימה. במקרה חירום רפואי יש לפנות מיד למוקד 101.
                  </p>
                ) : message._ambiguousRacesCTA ? (
                  <div className="max-w-[85%] rounded-2xl p-4 bg-surface border border-border">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-text-body font-semibold text-sm">לאיזה מרוץ התכוונת?</span>
                    </div>
                    <div className="text-text-muted text-xs mb-3">
                      חיפשת "<span className="text-text-body font-semibold">{message._ambiguousRacesCTA.user_typed}</span>" — מצאתי {message._ambiguousRacesCTA.options.length} מרוצים מתאימים ברשימה שלך:
                    </div>
                    <div className="space-y-2">
                      {message._ambiguousRacesCTA.options.map((opt) => (
                        <button
                          key={opt.id}
                          onClick={() => onNavigateToWeeklyPlan && onNavigateToWeeklyPlan()}
                          className="w-full min-h-[44px] py-2 px-3 rounded-xl text-text text-sm text-right border border-border"
                        >
                          <div className="font-semibold">{opt.race_name}</div>
                          <div className="text-xs text-text-muted mt-0.5">{opt.race_date}{opt.distance ? ` · ${opt.distance}` : ''}</div>
                        </button>
                      ))}
                    </div>
                  </div>
                ) : message._deepRefresh ? (
                  <div className="max-w-[85%] rounded-2xl p-4 bg-surface border border-border" data-testid="deep-refresh">
                    <div className="text-text text-sm mb-3">
                      הניתוח המעמיק עוד לא הופיע כאן. אם הוא עדיין נכתב, הוא יישמר בשיחה ברגע שיסתיים.
                    </div>
                    <button
                      onClick={() => loadChatHistory()}
                      className="w-full min-h-[44px] py-2 rounded-full text-sm btn-primary"
                    >
                      רענן את השיחה
                    </button>
                  </div>
                ) : message._missingRaceCTA ? (
                  <div className="max-w-[85%] rounded-2xl p-4 bg-surface border border-border">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-text-body font-semibold text-sm">מרוץ חדש</span>
                    </div>
                    <div className="text-text text-sm mb-3">
                      <div><span className="text-text-muted">שם:</span> <span className="font-semibold">{message._missingRaceCTA.race_name}</span></div>
                      <div><span className="text-text-muted">תאריך:</span> <span className="font-semibold">{message._missingRaceCTA.race_date}</span></div>
                    </div>
                    <p className="text-xs text-text-muted mb-3">
                      תצטרך להשלים מרחק יעד וזמן יעד בטופס.
                    </p>
                    <button
                      onClick={() => onNavigateToRaces && onNavigateToRaces(message._missingRaceCTA)}
                      className="w-full min-h-[44px] py-2 rounded-full text-sm btn-primary"
                    >
                      הוסף מרוץ חדש
                    </button>
                  </div>
                ) : message._planCTA ? (
                  <div className="max-w-[85%] rounded-2xl p-4 bg-surface border border-border">
                    {message._planCTA.type === 'race' ? (
                      <>
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-text-body font-semibold text-sm">
                            {message._planCTA.category === 'taper' ? 'שבוע מרוץ' : 'מרוץ מתקרב'}
                          </span>
                        </div>
                        <div className="text-text text-sm mb-3">
                          <div><span className="text-text-muted">מרוץ:</span> <span className="font-semibold">{message._planCTA.race_name}</span></div>
                          <div><span className="text-text-muted">תאריך:</span> <span className="font-semibold">{message._planCTA.race_date}</span> (בעוד {message._planCTA.days_until} ימים)</div>
                        </div>
                        <button
                          onClick={() => onNavigateToWeeklyPlan && onNavigateToWeeklyPlan()}
                          className="w-full min-h-[44px] py-2 rounded-full text-sm btn-primary"
                        >
                          צור תכנית ל-{message._planCTA.race_name}
                        </button>
                      </>
                    ) : (
                      <>
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-text-body font-semibold text-sm">תכנית כללית</span>
                        </div>
                        <div className="text-text text-sm mb-3">
                          <div><span className="text-text-muted">מרחק יעד:</span> <span className="font-semibold">{message._planCTA.goal_distance}</span></div>
                          {message._planCTA.goal_time && (
                            <div><span className="text-text-muted">זמן יעד:</span> <span className="font-semibold">{message._planCTA.goal_time}</span></div>
                          )}
                        </div>
                        <button
                          onClick={() => onNavigateToWeeklyPlan && onNavigateToWeeklyPlan()}
                          className="w-full min-h-[44px] py-2 rounded-full text-sm btn-primary"
                        >
                          צור תוכנית כללית לפי מרחק יעד
                        </button>
                      </>
                    )}
                  </div>
                ) : (
                  // Standard message bubble — asymmetric corner radius gives the "tail" effect toward the
                  // bubble's side. Graphite: user = line colour, coach = card colour with a line border.
                  <div className={`max-w-[85%] flex flex-col gap-2 ${message.isUser ? 'items-start' : 'items-end'}`}>
                    <div className={`max-w-full rounded-2xl px-4 py-3 ${
                      message.isUser
                        ? 'bg-border rounded-br-sm'
                        : 'bg-surface border border-border rounded-bl-sm'
                    }`}>
                      <div className="text-[15px] leading-relaxed whitespace-pre-wrap text-text">
                        {formatMessage(message.text)}
                      </div>
                      <div className="text-xs mt-1.5 text-text-muted">
                        {message.timestamp}
                      </div>
                    </div>
                    {/* The coach recommends changing participation in an upcoming race -> a way to the race list.
                        Navigation only: nothing here changes a race or the plan (utils/raceAction.js). */}
                    {!message.isUser && onNavigateToRaces && suggestsRaceChange(message.text, { nextRace }) && (
                      <button type="button" onClick={() => onNavigateToRaces()} data-testid="open-races"
                              className="h-11 px-4 rounded-full border border-border text-text-body text-[14px] font-semibold inline-flex items-center gap-2">
                        <FlagCheckered size={16} weight="bold" aria-hidden="true" />
                        מעבר ללוח התחרויות
                      </button>
                    )}
                    {/* A permanent plan-shaping setting (volume, days, goal, level, injury state…) is changed in the
                        profile — a way there, at that field's card. Navigation only (utils/profileAction.js). */}
                    {!message.isUser && onNavigateToProfile && profileSectionFor(message.text) && (
                      <button type="button" onClick={() => onNavigateToProfile(profileSectionFor(message.text))}
                              data-testid="open-profile"
                              className="h-11 px-4 rounded-full border border-border text-text-body text-[14px] font-semibold inline-flex items-center gap-2">
                        <UserCircle size={16} weight="bold" aria-hidden="true" />
                        מעבר לפרופיל
                      </button>
                    )}
                  </div>
                )}
              </div>
            ))}
            
            {/* Loading indicator — staged progress so the user knows what's
                happening over the 0–180s wait when the chat triggers a plan
                modification (tool-call). Stage text shifts with elapsed
                seconds from the OLDEST in-flight send. */}
            {inFlightCount > 0 && (() => {
              const _elapsed = oldestSendStartedAt ? Math.floor((Date.now() - oldestSendStartedAt) / 1000) : 0;
              let _stage = 'חושב...';
              let _hint = null;
              let _pct = 10;
              if (_elapsed < 5) {
                _stage = 'חושב...'; _pct = 5;
              } else if (_elapsed < 15) {
                _stage = 'מעבד את הבקשה...'; _pct = 15;
              } else if (_elapsed < 40) {
                // Generic — the chat may be analysing a workout OR updating the plan.
                _stage = 'המאמן עובד על התשובה...'; _hint = 'יכול לקחת עד 2 דקות'; _pct = 35;
              } else if (_elapsed < 90) {
                _stage = 'עוד רגע, כמעט מוכן...'; _hint = `${_elapsed}s שעברו, ממשיך`; _pct = 65;
              } else if (_elapsed < 150) {
                _stage = 'כמעט סיימנו, אל תסגור את הדף...'; _hint = `${_elapsed}s שעברו`; _pct = 85;
              } else {
                _stage = 'לוקח יותר מהצפוי, סגור את הדף ונסה שוב אם זה לא נגמר'; _hint = `${_elapsed}s שעברו`; _pct = 95;
              }
              return (
                <div className="flex justify-end">
                  <div className="bg-surface border border-border rounded-2xl px-4 py-3 min-w-[240px]">
                    <div className="flex items-center gap-2 text-text-body text-sm">
                      <span>{_stage}</span>
                    </div>
                    {_hint && (
                      <div className="text-text-muted text-xs mt-1">{_hint}</div>
                    )}
                    <div className="mt-2 h-1 bg-pressed rounded-full overflow-hidden">
                      <div
                        className="h-full bg-accent transition-all duration-1000"
                        style={{ width: `${_pct}%` }}
                      />
                    </div>
                    {inFlightCount > 1 && (
                      <div className="text-text-muted text-[10px] mt-1.5">{inFlightCount} שיחות במקביל</div>
                    )}
                  </div>
                </div>
              );
            })()}

            {/* Deep-analysis running: immediate, persistent feedback so the user KNOWS a
                heavy background job is in progress. Shown whenever no chat request is in
                flight (the dead window before the auto-send fires + the 30-60s BG-poll
                window); the generic spinner above covers the in-flight moment. */}
            {deepBusy && inFlightCount === 0 && (
              <div className="flex justify-end">
                <div className="bg-surface border border-border rounded-2xl px-4 py-3 min-w-[240px]">
                  <div className="flex items-center gap-2 text-text-body text-sm">
                    <span>המאמן מנתח את האימון לעומק. התהליך עשוי להימשך מספר דקות.</span>
                  </div>
                  <div className="mt-2 h-1 bg-pressed rounded-full overflow-hidden">
                    <div className="h-full bg-accent animate-pulse" style={{ width: '60%' }} />
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>
        </div>
      </main>

      {/* The daily limit reached: the card in the input area's place (the server's reset time; back to the input
          by itself when the countdown ends). One urgent message can still be written — the server decides. */}
      {limitReached && !isTrialExpired && (
        <div className="sticky bottom-0 z-10 bg-bg pt-2">
          <DailyLimitCard limit={dailyLimit} trialDaysLeft={trialDaysLeft} onTrial={userPlan === 'trial'}
                          resetsAt={resetsAt}
                          onReset={() => { loadDailyMessageCount(); setTimeout(loadDailyMessageCount, 30000); }}
                          onUrgent={urgentOpen ? undefined : () => setUrgentOpen(true)} />
        </div>
      )}

      {/* Persistent action buttons + dynamic chips */}
      {!(limitReached && !isTrialExpired) && (
      <div className="sticky bottom-0 z-10 bg-bg border-t border-border px-4 pt-3 pb-1">
        <div className="container mx-auto max-w-lg">
          {/* Dynamic context chips */}
          {!isLoading && (() => {
            const chips = [];
            const today = new Date();
            // Race proximity
            let daysToRace = null;
            let raceName = '';
            if (nextRace?.race_date) {
              daysToRace = Math.ceil((new Date(nextRace.race_date + 'T00:00:00') - today) / 86400000);
              raceName = nextRace.race_name || 'התחרות';
            }

            // Sport context for label phrasing (2026-06-11):
            // cycling-only athletes shouldn't see "ריצה ארוכה" prompts.
            // Logic: if only cycling is active → cycling phrasing.
            // Otherwise default to running (covers single-sport runners
            // AND multi-sport athletes — running references stay relevant).
            const _isCyclingOnly = sportsActive.includes('cycling') && !sportsActive.includes('running');
            const _activityWord = _isCyclingOnly ? 'הרכיבה' : 'הריצה';
            const _longWorkoutWord = _isCyclingOnly ? 'רכיבה ארוכה' : 'ריצה ארוכה';

            // 1. Today's activity analysis
            if (todayActivity) {
              const dist = todayActivity.distance ? `${todayActivity.distance}ק"מ` : '';
              chips.push({ icon: '📊', label: `נתח את ${_activityWord}${dist ? ` (${dist})` : ''} מהיום`, msg: `נתח בבקשה את ${_activityWord} שלי מהיום${dist ? ` (${dist})` : ''}` });
            }

            // 2. Race-specific chips
            if (daysToRace !== null && daysToRace > 0 && daysToRace <= 7) {
              chips.push({ icon: '🍏', label: `מה לאכול לפני ${raceName}?`, msg: `${raceName} בעוד ${daysToRace} ימים. מה כדאי לאכול לפניו?` });
              chips.push({ icon: '🏁', label: `איך להתכונן ל${raceName}?`, msg: `יש לי ${raceName} בעוד ${daysToRace} ימים. איך להתכונן נכון?` });
            } else if (daysToRace !== null && daysToRace > 7 && daysToRace <= 14) {
              chips.push({ icon: '📅', label: `${daysToRace} ימים ל${raceName} — מה מיקוד השבוע?`, msg: `נשארו ${daysToRace} ימים ל${raceName}. מה צריך להיות מיקוד האימונים השבוע?` });
            } else {
              // No imminent race — sport-aware nutrition prompt
              chips.push({ icon: '🍏', label: `מה לאכול לפני ${_longWorkoutWord}?`, msg: `מה כדאי לאכול לפני ${_longWorkoutWord}?` });
            }

            // 3. Injury chip
            if (injuryArea) {
              const area = injuryArea.split('/')[0].trim();
              chips.push({ icon: '🩹', label: `שמירה על ${area} השבוע`, msg: `יש לי רגישות ב${injuryArea}. איך לשמור על זה השבוע בתוך התכנית?` });
            }

            // 4. Always-on generic chips (2026-06-11) — give the athlete
            // useful starting points even when there's no race/Strava/injury.
            // Sport-aware: cycling-only sees cycling phrasing.
            chips.push({
              icon: '📅',
              label: 'מה האימון של היום?',
              msg: 'מה האימון שלי להיום? תסביר את המבנה והעצימות.',
            });
            chips.push({
              icon: '📊',
              label: `איך מצב העומס השבוע?`,
              msg: `איך אני נראה השבוע מבחינת עומס ועייפות? יש משהו שצריך להתאים?`,
            });
            if (_isCyclingOnly) {
              chips.push({
                icon: '💪',
                label: 'איך לשפר את ה-FTP?',
                msg: 'מה הדרך הכי טובה לשפר את ה-FTP שלי בחודש הקרוב?',
              });
              chips.push({
                icon: '⛰️',
                label: 'אימון טיפוסים מומלץ?',
                msg: 'תמליץ לי על אימון טיפוסים שיתאים לרמה שלי.',
              });
            } else {
              chips.push({
                icon: '⚡',
                label: 'מה הקצבים שלי?',
                msg: 'תסביר לי את הקצבים: קל, טמפו, מטרת מרוץ, ארוך. כל אחד באיזה דופק/RPE?',
              });
              chips.push({
                icon: '🏃',
                label: 'מה אימון הקצב הבא?',
                msg: 'מה אימון הקצב/אינטרוולים הבא שלי השבוע? תסביר את המבנה.',
              });
            }

            // Graphite: outline chips in one scrollable row; "התכנית השבועית שלי" leads, outlined in lime.
            const CHIP = 'flex-shrink-0 min-h-[44px] px-4 rounded-full text-[13px] font-medium border whitespace-nowrap';
            return (
              <div className="flex gap-2 overflow-x-auto scrollbar-hide pb-1">
                {onNavigateToWeeklyPlan && (
                  <button type="button" onClick={() => onNavigateToWeeklyPlan()}
                          className={`${CHIP} border-accent text-accent font-bold`}>
                    התכנית השבועית שלי
                  </button>
                )}
                {chips.map((c, i) => (
                  <button key={i} type="button" onClick={() => setInputValue(c.msg)}
                          className={`${CHIP} border-border text-text-body`}>
                    {c.label}
                  </button>
                ))}
              </div>
            );
          })()}
        </div>
      </div>
      )}

      {/* Input — design-token aligned. Hidden behind the limit card; open for one urgent message (urgentOpen). */}
      {(!limitReached || isTrialExpired || urgentOpen) && (
      <footer className="sticky bottom-0 bg-bg px-4 pt-2 pb-4">
        <div className="container mx-auto max-w-lg">
          {!limitReached && !isTrialExpired && remainingMessages >= 1 && remainingMessages <= 2 && (
            <QuotaApproaching remaining={remainingMessages} limit={dailyLimit} />
          )}
          {limitReached && urgentOpen && (
            <p className="m-0 pb-2 text-[13px] text-text-body" data-testid="limit-urgent-hint">
              תאר בקצרה מה קורה, וההודעה תיבדק. במקרה חירום רפואי יש לפנות מיד למוקד 101.
            </p>
          )}
          <div className="flex gap-2">
            <input
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSend()}
              placeholder={isTrialExpired ? "שדרג ל-Pro להמשך שימוש..." : urgentOpen ? "תאר מה קורה..." : "כתוב למאמן..."}
              // 2026-06-06 final: re-opened the input even while isLoading.
              // The previous re-lock (commit 80e2969) was a workaround
              // for Render-restart race conditions when a chat send
              // hit the same worker that was running a slow Pro revise
              // synchronously. The BG-task refactor (commit 17d526f)
              // moved revise to a FastAPI BackgroundTask: /chat now
              // returns in ~10s (Gemini tool-detection + Flash-Lite
              // confirmation text), and the slow revise() runs on a
              // separate task without holding the request worker.
              // Worker contention is gone, so it's safe to let the
              // user keep chatting while updates run in the background.
              disabled={isTrialExpired}
              className={`flex-1 min-w-0 h-12 px-5 rounded-full bg-surface border text-text placeholder-text-muted text-[15px] focus:outline-none disabled:opacity-50 transition-colors ${
                isTrialExpired
                  ? 'border-border cursor-not-allowed'
                  : 'border-border focus:border-accent'
              }`}
            />
            <button
              type="button"
              onClick={handleSend}
              disabled={!inputValue.trim() || isTrialExpired}
              aria-label="שלח"
              className="w-12 h-12 shrink-0 rounded-full btn-primary flex items-center justify-center"
            >
              <ArrowUp size={22} weight="bold" />
            </button>
          </div>
        </div>
      </footer>
      )}
    </div>
    </div>
  );
}

export default Chat;
